/**
 * Utilidad de validación centralizada para formularios de actuaciones.
 * Valida todos los formularios desde sessionStorage antes de guardar.
 *
 * @module utils/formValidation
 */

import debug from 'debug';
import { createLocationSchema } from '../components/forms/ActionForms/LocationForm/schema';
import { createOperatorsAndVehicleSchema } from '../components/forms/ActionForms/OperatorsAndVehicles/schema';
import { EditingActionSchema } from '../components/forms/ActionForms/ActionEdition/schema';
import { basicScheduleContextFromSession } from '../components/forms/ActionForms/ActionEdition/basicSchedule';
import {
  formActionNaming,
  formActionValidationNaming,
} from '../components/forms/ActionForms/constants';
import { decompressSessionStorage, getSessionStorageUser } from './sessionStorage';
import { roleCanViewBudgets } from './rolePermissions';
import i18n from '../i18n';

// Debug loggers
const log = debug('rois:utils:form-validation');
const logMain = debug('rois:utils:form-validation:main');
const logOperators = debug('rois:utils:form-validation:operators');
const logLocation = debug('rois:utils:form-validation:location');
const logEdition = debug('rois:utils:form-validation:edition');
const logMaterials = debug('rois:utils:form-validation:materials');
const logMachinery = debug('rois:utils:form-validation:machinery');
const logAnimals = debug('rois:utils:form-validation:animals');
const logBudgetItems = debug('rois:utils:form-validation:budgetItems');
const logSession = debug('rois:utils:form-validation:session');

// Enable debug in development
if (import.meta.env.DEV) {
  debug.enable('rois:utils:form-validation*');
}

export interface ValidationError {
  tab: string;
  tabKey: string;
  field: string;
  message: string;
}

export interface TabValidationStatus {
  tabKey: string;
  tabLabel: string;
  hasErrors: boolean;
  errorCount: number;
}

export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
  tabsWithErrors: TabValidationStatus[];
}

interface ValidationOptions {
  isNewAction: boolean;
  showHoursType?: boolean;
  /** SR/FR-9.3 (2026-07-04): en el wizard del RV la tab Operarios está
   *  oculta — los operarios/vehículos se HEREDAN de la ruta en el backend.
   *  Exigir ≥1 operario del form bloqueaba el guardado final para siempre. */
  skipOperatorsValidation?: boolean;
}

/**
 * Lista todas las keys de sessionStorage que empiezan con un prefijo
 */
function listSessionStorageKeys(prefix: string): string[] {
  const keys: string[] = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key && key.startsWith(prefix)) {
      keys.push(key);
    }
  }
  return keys;
}

/**
 * Obtiene datos de un formulario desde sessionStorage (comprimidos)
 * Usado por useCacheFormInSessionStorage
 */
function getFormData(key: string): any {
  logSession('═══════════════════════════════════════════════════════════════');
  logSession(`📥 Intentando leer sessionStorage key (comprimida): "${key}"`);

  try {
    const raw = sessionStorage.getItem(key);
    logSession(`   Raw data exists: ${raw !== null}`);
    logSession(`   Raw data length: ${raw?.length || 0}`);

    if (!raw) {
      logSession(`   ❌ No hay datos para key: ${key}`);

      // Listar keys similares para debug
      const allKeys = listSessionStorageKeys('new-');
      logSession(`   📋 Keys en sessionStorage que empiezan con 'new-':`);
      allKeys.forEach(k => {
        logSession(`      - ${k}`);
      });

      return null;
    }

    const decompressed = decompressSessionStorage(raw);
    logSession(`   Decompressed length: ${decompressed?.length || 0}`);

    const parsed = JSON.parse(decompressed);
    logSession(`   ✅ Datos parseados correctamente`);
    logSession(`   Keys en datos: ${Object.keys(parsed).join(', ')}`);

    return parsed;
  } catch (error) {
    console.error(`❌ Error parsing form data for key ${key}:`, error);
    return null;
  }
}

/**
 * Obtiene datos de un formulario desde sessionStorage (sin comprimir)
 * Usado por el guardado de tabs interno/externo
 */
function getFormDataUncompressed(key: string): any {
  logSession('═══════════════════════════════════════════════════════════════');
  logSession(`📥 Intentando leer sessionStorage key (sin comprimir): "${key}"`);

  try {
    const raw = sessionStorage.getItem(key);
    logSession(`   Raw data exists: ${raw !== null}`);
    logSession(`   Raw data length: ${raw?.length || 0}`);

    if (!raw) {
      logSession(`   ❌ No hay datos para key: ${key}`);
      return null;
    }

    // Los datos de _interno se guardan SIN comprimir (JSON.stringify directo)
    const parsed = JSON.parse(raw);
    logSession(`   ✅ Datos parseados correctamente (sin descomprimir)`);
    logSession(`   Keys en datos: ${Object.keys(parsed).join(', ')}`);

    return parsed;
  } catch (error) {
    console.error(`❌ Error parsing uncompressed form data for key ${key}:`, error);
    return null;
  }
}

/**
 * Normaliza el mensaje de error de Yup
 * Puede ser string, función, u objeto con key de i18n
 */
function normalizeErrorMessage(message: any): string {
  if (typeof message === 'function') {
    return String(message());
  }
  if (typeof message === 'object' && message !== null) {
    // Si es un objeto (key de i18n compleja), intentar traducir o usar fallback
    return message?.key
      ? String(i18n.t(message.key, message.values || {}))
      : String(i18n.t('validation:required'));
  }
  if (typeof message === 'string') {
    // Si es un string, intentar traducir (por si es una key de i18n)
    const translated = i18n.t(message);
    return typeof translated === 'string' ? translated : message;
  }
  return String(i18n.t('validation:required'));
}

/**
 * Mapa de nombres de tabs para mostrar al usuario (fallback)
 */
const TAB_LABELS: Record<string, string> = {
  'operators-and-vehicles': 'Operarios',
  'location': 'Ubicación',
  'edition': 'Datos Act.',
  'budget-items': 'Partidas',
  'materials': 'Materiales',
  'machinery': 'Maquinaria Aux.',
  'animals': 'Animales',
};

/**
 * Obtiene la etiqueta traducida de una tab
 * Usa las traducciones de navigation:routes.{tabKey}
 */
function getTabLabel(tabKey: string): string {
  // Usar traducciones de navigation:routes que contienen los nombres de tabs
  const translationKey = `navigation:routes.${tabKey}`;
  const translation = i18n.t(translationKey);

  // Si la traducción es la misma que la key (no encontrada), usar el mapa de fallback
  if (translation === translationKey || !translation) {
    return TAB_LABELS[tabKey] || tabKey;
  }

  return translation;
}

/**
 * Claves de `sessionStorage` de cada pestaña: las del asistente de alta o las
 * de la validación. Compartidas por la validación completa y por la de
 * coherencia de fechas de «Guardar», para que lean lo mismo.
 */
const sessionFormKeys = (isNewAction: boolean) =>
  isNewAction
    ? {
        operatorsMain: formActionNaming.NEW_OPERATORS_AND_VEHICLE_FORM,
        operatorsInterno: `${formActionNaming.NEW_OPERATORS_AND_VEHICLE_FORM}_interno`,
        location: formActionNaming.NEW_LOCATION_FORM,
        edition: formActionNaming.NEW_EDITION_FORM,
        materials: formActionNaming.NEW_MATERIALS_FORM,
        workEquipment: formActionNaming.NEW_WORK_EQUIPMENT_FORM,
        animals: formActionNaming.NEW_ANIMALS_FORM,
        budgetItems: formActionNaming.NEW_BUDGET_ITEMS_FORM,
      }
    : {
        operatorsMain: formActionValidationNaming.NEW_VALIDATION_OPERATORS_AND_VEHICLE_FORM,
        operatorsInterno: `${formActionValidationNaming.NEW_VALIDATION_OPERATORS_AND_VEHICLE_FORM}_interno`,
        location: formActionValidationNaming.NEW_VALIDATION_LOCATION_FORM,
        edition: formActionValidationNaming.NEW_VALIDATION_EDITION_FORM,
        materials: formActionValidationNaming.NEW_VALIDATION_MATERIALS_FORM,
        workEquipment: formActionValidationNaming.NEW_VALIDATION_WORK_EQUIPMENT_FORM,
        animals: formActionValidationNaming.NEW_VALIDATION_ANIMALS_FORM,
        budgetItems: formActionValidationNaming.NEW_VALIDATION_BUDGET_ITEMS_FORM,
      };

/**
 * Valida todos los formularios de actuación desde sessionStorage.
 * Esta función se debe llamar ANTES de guardar la actuación.
 *
 * @param options - Opciones de validación
 * @returns Resultado de la validación con errores por tab
 */
export const validateAllFormsFromSessionStorage = async (
  options: ValidationOptions
): Promise<ValidationResult> => {
  const errors: ValidationError[] = [];
  const tabsWithErrors: TabValidationStatus[] = [];

  const { isNewAction, showHoursType = true, skipOperatorsValidation = false } = options;

  logMain('═══════════════════════════════════════════════════════════════════════');
  logMain('🔍 INICIANDO VALIDACIÓN CENTRALIZADA');
  logMain('═══════════════════════════════════════════════════════════════════════');
  logMain(`   isNewAction: ${isNewAction}`);
  logMain(`   showHoursType: ${showHoursType}`);

  // Determinar qué keys de sessionStorage usar
  // NOTA: Los operarios se guardan en DOS lugares:
  // 1. Key principal (comprimida) - por useCacheFormInSessionStorage
  // 2. Key _interno (sin comprimir) - solo cuando cambia de tab
  // Priorizamos la key principal que siempre tiene los datos actualizados
  const keys = sessionFormKeys(isNewAction);

  logMain('📋 Keys de sessionStorage a usar:');
  logMain(`   operatorsMain: "${keys.operatorsMain}"`);
  logMain(`   operatorsInterno: "${keys.operatorsInterno}"`);
  logMain(`   location: "${keys.location}"`);
  logMain(`   edition: "${keys.edition}"`);

  // ═══════════════════════════════════════════════════════════════
  // 1. VALIDAR OPERARIOS Y VEHÍCULOS
  // ═══════════════════════════════════════════════════════════════
  logOperators('═══════════════════════════════════════════════════════════════');
  logOperators('1️⃣ VALIDANDO OPERARIOS Y VEHÍCULOS');
  logOperators('═══════════════════════════════════════════════════════════════');

  if (skipOperatorsValidation) {
    logOperators('ℹ️ SKIP (contexto RV): operarios heredados de la ruta (FR-9.3)');
  }

  // Intentar leer de la key principal primero (comprimida por useCacheFormInSessionStorage)
  // Si no existe, intentar con _interno (sin comprimir, solo existe si cambió de tab)
  let operatorsData = skipOperatorsValidation ? null : getFormData(keys.operatorsMain);

  if (!operatorsData && !skipOperatorsValidation) {
    logOperators('⚠️ No hay datos en key principal, intentando con _interno...');
    operatorsData = getFormDataUncompressed(keys.operatorsInterno);
  }

  const operatorsTabKey = 'operators-and-vehicles';
  let operatorsErrorCount = 0;

  if (operatorsData) {
    logOperators('✅ Datos de operarios encontrados');
    logOperators(`   operatorsTable existe: ${!!operatorsData.operatorsTable}`);
    logOperators(`   operatorsTable es array: ${Array.isArray(operatorsData.operatorsTable)}`);

    if (operatorsData.operatorsTable) {
      const totalItems = operatorsData.operatorsTable.length;
      const validItems = operatorsData.operatorsTable.filter((op: any) => op);
      logOperators(`   operatorsTable length: ${totalItems}`);
      logOperators(`   operatorsTable items válidos (no null): ${validItems.length}`);

      // Log de los primeros 5 items
      logOperators('   Primeros 5 items de operatorsTable:');
      operatorsData.operatorsTable.slice(0, 5).forEach((item: any, index: number) => {
        if (item) {
          logOperators(`      [${index}] ${typeof item === 'object' ? JSON.stringify(item).substring(0, 100) : item}`);
        } else {
          logOperators(`      [${index}] NULL/UNDEFINED`);
        }
      });
    }

    logOperators(`   isExternal: ${operatorsData.isExternal}`);
    logOperators(`   externals existe: ${!!operatorsData.externals}`);
    logOperators(`   externals length: ${operatorsData.externals?.length || 0}`);
    logOperators(`   registerInitDate: ${operatorsData.registerInitDate}`);
    logOperators(`   totalHours: ${operatorsData.totalHours}`);

    // Validar con schema Yup
    logOperators('🔄 Validando con schema Yup...');
    try {
      await createOperatorsAndVehicleSchema(showHoursType).validate(
        operatorsData,
        { abortEarly: false }
      );
      logOperators('✅ Schema Yup: PASÓ');
    } catch (err: any) {
      logOperators('❌ Schema Yup: FALLÓ');
      if (err.inner && Array.isArray(err.inner)) {
        logOperators(`   Errores encontrados: ${err.inner.length}`);
        err.inner.forEach((error: any, index: number) => {
          logOperators(`   [${index}] Campo: ${error.path}, Mensaje: ${error.message}`);
          operatorsErrorCount++;
          errors.push({
            tab: getTabLabel(operatorsTabKey),
            tabKey: operatorsTabKey,
            field: error.path || 'general',
            message: normalizeErrorMessage(error.message),
          });
        });
      }
    }

    // Validación adicional: verificar que hay al menos un operario o es externo
    logOperators('🔄 Validación custom de operarios...');
    const hasOperators = operatorsData.operatorsTable?.filter((op: any) => op)?.length > 0;
    const isExternal = operatorsData.isExternal === true;
    const hasExternals = operatorsData.externals?.length > 0;

    logOperators(`   hasOperators (al menos 1 no-null): ${hasOperators}`);
    logOperators(`   isExternal (exactamente true): ${isExternal}`);
    logOperators(`   hasExternals (externals.length > 0): ${hasExternals}`);

    if (!hasOperators && !hasExternals && !skipOperatorsValidation) {
      logOperators('❌ ERROR: No hay operarios y no es trabajo externo');
      operatorsErrorCount++;
      errors.push({
        tab: getTabLabel(operatorsTabKey),
        tabKey: operatorsTabKey,
        field: 'operatorsTable',
        message: i18n.t('validation:operators.atLeastOne', 'Es necesario seleccionar al menos un operario'),
      });
    } else {
      logOperators('✅ Validación custom de operarios: PASÓ');
    }

    if (isExternal && !hasExternals) {
      logOperators('❌ ERROR: Es externo pero no hay empresas externas');
      operatorsErrorCount++;
      errors.push({
        tab: getTabLabel(operatorsTabKey),
        tabKey: operatorsTabKey,
        field: 'externals',
        message: i18n.t('validation:operators.externalRequired', 'Es necesario añadir al menos una empresa externa'),
      });
    }

    // ═══════════════════════════════════════════════════════════════
    // VALIDACIÓN ADICIONAL: Vehículos deben tener conductor asignado
    // ═══════════════════════════════════════════════════════════════
    logOperators('🔄 Validación de vehículos con conductor...');

    // vehicleTable es un Record<string, VehicleData> o array según el contexto
    const vehicleTable = operatorsData.vehicleTable || {};
    const vehicleEntries = Array.isArray(vehicleTable)
      ? vehicleTable.filter((v: any) => v) // Filtrar nulls si es array
      : Object.values(vehicleTable);

    logOperators(`   vehicleTable entries: ${vehicleEntries.length}`);

    if (vehicleEntries.length > 0) {
      vehicleEntries.forEach((vehicle: any, index: number) => {
        // Verificar si tiene conductor asignado (puede estar en driverID o driver)
        const hasDriver = vehicle?.driverID || vehicle?.driver;
        // Verificar si tiene ID de vehículo (puede estar en vehicleID o id)
        const hasVehicleId = vehicle?.vehicleID || vehicle?.id;

        logOperators(`   Vehicle [${index}]: plate=${vehicle?.plate || 'N/A'}, hasDriver=${!!hasDriver}, hasId=${!!hasVehicleId}`);

        // Si hay vehículo seleccionado pero no tiene conductor, es error
        if (hasVehicleId && !hasDriver) {
          logOperators(`❌ ERROR: Vehículo "${vehicle?.plate || index}" sin conductor asignado`);
          operatorsErrorCount++;
          errors.push({
            tab: getTabLabel(operatorsTabKey),
            tabKey: operatorsTabKey,
            field: `vehicleTable.${index}.driverID`,
            message: i18n.t(
              'validation:vehicles.driverRequired',
              'Es necesario asignar conductor al vehículo seleccionado'
            ),
          });
        }
      });
    } else {
      logOperators('ℹ️ No hay vehículos seleccionados - SKIP validación de conductores');
    }
  } else if (skipOperatorsValidation) {
    // SR/FR-9.3: sin datos y sin error — operarios heredados de la ruta.
    logOperators('ℹ️ SKIP (contexto RV): sin datos de operarios, heredados de la ruta');
  } else {
    // Si no hay datos, es un error
    logOperators('❌ NO HAY DATOS de operarios en sessionStorage');
    operatorsErrorCount++;
    errors.push({
      tab: getTabLabel(operatorsTabKey),
      tabKey: operatorsTabKey,
      field: 'general',
      message: i18n.t('validation:tab.notCompleted', 'No s\'han completat les dades d\'aquesta pestanya'),
    });
  }

  logOperators(`📊 RESUMEN OPERARIOS: ${operatorsErrorCount} errores`);

  if (operatorsErrorCount > 0) {
    tabsWithErrors.push({
      tabKey: operatorsTabKey,
      tabLabel: getTabLabel(operatorsTabKey),
      hasErrors: true,
      errorCount: operatorsErrorCount,
    });
  }

  // ═══════════════════════════════════════════════════════════════
  // 2. VALIDAR UBICACIÓN
  // ═══════════════════════════════════════════════════════════════
  logLocation('═══════════════════════════════════════════════════════════════');
  logLocation('2️⃣ VALIDANDO UBICACIÓN');
  logLocation('═══════════════════════════════════════════════════════════════');

  const locationData = getFormData(keys.location);
  const locationTabKey = 'location';
  let locationErrorCount = 0;

  if (locationData) {
    logLocation('✅ Datos de ubicación encontrados');
    logLocation(`   Keys: ${Object.keys(locationData).join(', ')}`);

    try {
      // marginRequired=false: este validador corre en un route action (sin
      // hooks → sin acceso al flag showMargin). El propio LocationForm ya
      // exige margin cuando el campo es visible; aquí exigirlo bloqueaba el
      // guardado final en tenants con el flag apagado (fix 2026-07-04).
      await createLocationSchema(true, false).validate(locationData, {
        abortEarly: false,
      });
      logLocation('✅ Schema Yup de ubicación: PASÓ');
    } catch (err: any) {
      logLocation('❌ Schema Yup de ubicación: FALLÓ');
      if (err.inner && Array.isArray(err.inner)) {
        err.inner.forEach((error: any) => {
          locationErrorCount++;
          errors.push({
            tab: getTabLabel(locationTabKey),
            tabKey: locationTabKey,
            field: error.path || 'general',
            message: normalizeErrorMessage(error.message),
          });
        });
      }
    }
  } else {
    // ✅ FIX: Si no hay datos en sessionStorage, el usuario NO visitó esta tab
    // En NewAction: SKIP validación (usuario no visitó = no validar)
    // En ActionsValidation: SKIP validación (actuación existente tiene datos en servidor)
    logLocation('ℹ️ Location: Usuario no visitó esta pestaña - SKIP validación');
  }

  logLocation(`📊 RESUMEN UBICACIÓN: ${locationErrorCount} errores`);

  if (locationErrorCount > 0) {
    tabsWithErrors.push({
      tabKey: locationTabKey,
      tabLabel: getTabLabel(locationTabKey),
      hasErrors: true,
      errorCount: locationErrorCount,
    });
  }

  // ═══════════════════════════════════════════════════════════════
  // 3. VALIDAR DATOS DE ACTUACIÓN (EDITION)
  // ═══════════════════════════════════════════════════════════════
  logEdition('═══════════════════════════════════════════════════════════════');
  logEdition('3️⃣ VALIDANDO DATOS DE ACTUACIÓN (EDITION)');
  logEdition('═══════════════════════════════════════════════════════════════');

  const editionData = getFormData(keys.edition);
  const editionTabKey = 'edition';
  let editionErrorCount = 0;

  if (editionData) {
    logEdition('✅ Datos de edición encontrados');
    logEdition(`   Keys: ${Object.keys(editionData).join(', ')}`);

    try {
      // Horario de la básica (revisión 12-oct, R2): la regla fin ≥ inicio solo
      // si quien valida lo puede tocar. El contexto sale de la MISMA función
      // que usa el resumen, para que no se vuelvan a separar.
      await EditingActionSchema.validate(editionData, {
        abortEarly: false,
        context: basicScheduleContextFromSession(
          isNewAction ? 'wizard' : 'validation',
          editionData
        ),
      });
      logEdition('✅ Schema Yup de edición: PASÓ');
    } catch (err: any) {
      logEdition('❌ Schema Yup de edición: FALLÓ');
      if (err.inner && Array.isArray(err.inner)) {
        err.inner.forEach((error: any) => {
          editionErrorCount++;
          errors.push({
            tab: getTabLabel(editionTabKey),
            tabKey: editionTabKey,
            field: error.path || 'general',
            message: normalizeErrorMessage(error.message),
          });
        });
      }
    }
  } else {
    // ✅ FIX: Si no hay datos en sessionStorage, el usuario NO visitó esta tab
    // En NewAction: SKIP validación (usuario no visitó = no validar)
    // En ActionsValidation: SKIP validación (actuación existente tiene datos en servidor)
    logEdition('ℹ️ Edition: Usuario no visitó esta pestaña - SKIP validación');
  }

  logEdition(`📊 RESUMEN EDICIÓN: ${editionErrorCount} errores`);

  if (editionErrorCount > 0) {
    tabsWithErrors.push({
      tabKey: editionTabKey,
      tabLabel: getTabLabel(editionTabKey),
      hasErrors: true,
      errorCount: editionErrorCount,
    });
  }

  // ═══════════════════════════════════════════════════════════════
  // 4. VALIDAR MATERIALES
  // ═══════════════════════════════════════════════════════════════
  logMaterials('═══════════════════════════════════════════════════════════════');
  logMaterials('4️⃣ VALIDANDO MATERIALES');
  logMaterials('═══════════════════════════════════════════════════════════════');

  const materialsData = getFormData(keys.materials);
  const materialsTabKey = 'materials';
  let materialsErrorCount = 0;

  if (materialsData) {
    logMaterials('✅ Datos de materiales encontrados');
    logMaterials(`   isMaterialTableDisplayed: ${materialsData.isMaterialTableDisplayed}`);
    logMaterials(`   materialsTable keys: ${Object.keys(materialsData.materialsTable || {}).length}`);

    // Solo validar si el switch está activado (Si hay)
    if (materialsData.isMaterialTableDisplayed === 'true') {
      const materialsTable = materialsData.materialsTable || {};
      const materialEntries = Object.values(materialsTable);

      logMaterials(`   Materiales añadidos: ${materialEntries.length}`);

      // Validar que hay al menos un material
      if (materialEntries.length === 0) {
        logMaterials('❌ ERROR: Switch activado pero no hay materiales');
        materialsErrorCount++;
        errors.push({
          tab: getTabLabel(materialsTabKey),
          tabKey: materialsTabKey,
          field: 'materialsTable',
          message: i18n.t('validation:materials.atLeastOne', 'Es necesario añadir al menos un material'),
        });
      } else {
        // Validar que cada material tenga unidades
        materialEntries.forEach((material: any, index: number) => {
          const ut = material?.ut;
          logMaterials(`   Material [${index}]: ut="${ut}" (tipo: ${typeof ut})`);

          if (ut === undefined || ut === null || ut === '' || Number(ut) <= 0) {
            logMaterials(`❌ ERROR: Material sin unidades válidas`);
            materialsErrorCount++;
            errors.push({
              tab: getTabLabel(materialsTabKey),
              tabKey: materialsTabKey,
              field: `materialsTable.${index}.ut`,
              message: i18n.t('validation:materials.unitRequired', 'Es necesario indicar las unidades del material'),
            });
          }
        });
      }
    } else {
      logMaterials('ℹ️ Switch desactivado - no se requieren materiales');
    }
  } else {
    logMaterials('ℹ️ No hay datos de materiales en sessionStorage - SKIP');
  }

  logMaterials(`📊 RESUMEN MATERIALES: ${materialsErrorCount} errores`);

  if (materialsErrorCount > 0) {
    tabsWithErrors.push({
      tabKey: materialsTabKey,
      tabLabel: getTabLabel(materialsTabKey),
      hasErrors: true,
      errorCount: materialsErrorCount,
    });
  }

  // ═══════════════════════════════════════════════════════════════
  // 5. VALIDAR MAQUINARIA AUXILIAR (Work Equipment)
  // ═══════════════════════════════════════════════════════════════
  logMachinery('═══════════════════════════════════════════════════════════════');
  logMachinery('5️⃣ VALIDANDO MAQUINARIA AUXILIAR');
  logMachinery('═══════════════════════════════════════════════════════════════');

  const workEquipmentData = getFormData(keys.workEquipment);
  const machineryTabKey = 'machinery';
  let machineryErrorCount = 0;

  if (workEquipmentData) {
    logMachinery('✅ Datos de equipamiento encontrados');
    logMachinery(`   isWorkEquipmentDisplayed: ${workEquipmentData.isWorkEquipmentDisplayed}`);
    logMachinery(`   machineryTable keys: ${Object.keys(workEquipmentData.machineryTable || {}).length}`);

    // Solo validar si el switch está activado
    if (workEquipmentData.isWorkEquipmentDisplayed === 'true') {
      const machineryTable = workEquipmentData.machineryTable || {};
      const machineryEntries = Object.values(machineryTable);

      logMachinery(`   Maquinaria añadida: ${machineryEntries.length}`);

      // Validar que cada maquinaria tenga unidades (si hay alguna)
      machineryEntries.forEach((machinery: any, index: number) => {
        const ut = machinery?.ut;
        logMachinery(`   Maquinaria [${index}]: ut="${ut}" (tipo: ${typeof ut})`);

        if (ut === undefined || ut === null || ut === '' || Number(ut) <= 0) {
          logMachinery(`❌ ERROR: Maquinaria sin unidades válidas`);
          machineryErrorCount++;
          errors.push({
            tab: getTabLabel(machineryTabKey),
            tabKey: machineryTabKey,
            field: `machineryTable.${index}.ut`,
            message: i18n.t('validation:machinery.unitRequired', 'Es necesario indicar las unidades de la maquinaria'),
          });
        }
      });
    } else {
      logMachinery('ℹ️ Switch desactivado - no se requiere maquinaria');
    }
  } else {
    logMachinery('ℹ️ No hay datos de equipamiento en sessionStorage - SKIP');
  }

  logMachinery(`📊 RESUMEN MAQUINARIA: ${machineryErrorCount} errores`);

  if (machineryErrorCount > 0) {
    tabsWithErrors.push({
      tabKey: machineryTabKey,
      tabLabel: getTabLabel(machineryTabKey),
      hasErrors: true,
      errorCount: machineryErrorCount,
    });
  }

  // ═══════════════════════════════════════════════════════════════
  // 6. VALIDAR ANIMALES
  // ═══════════════════════════════════════════════════════════════
  logAnimals('═══════════════════════════════════════════════════════════════');
  logAnimals('6️⃣ VALIDANDO ANIMALES');
  logAnimals('═══════════════════════════════════════════════════════════════');

  const animalsData = getFormData(keys.animals);
  const animalsTabKey = 'animals';
  let animalsErrorCount = 0;

  if (animalsData) {
    logAnimals('✅ Datos de animales encontrados');
    logAnimals(`   isAnimalsTableDisplayed: ${animalsData.isAnimalsTableDisplayed}`);
    logAnimals(`   animalsTable keys: ${Object.keys(animalsData.animalsTable || {}).length}`);

    // Solo validar si el switch está activado
    if (animalsData.isAnimalsTableDisplayed === 'true') {
      const animalsTable = animalsData.animalsTable || {};
      const animalEntries = Object.values(animalsTable);

      logAnimals(`   Animales añadidos: ${animalEntries.length}`);

      // Validar que hay al menos un animal
      if (animalEntries.length === 0) {
        logAnimals('❌ ERROR: Switch activado pero no hay animales');
        animalsErrorCount++;
        errors.push({
          tab: getTabLabel(animalsTabKey),
          tabKey: animalsTabKey,
          field: 'animalsTable',
          message: i18n.t('validation:animals.atLeastOne', 'Es necesario añadir al menos un animal'),
        });
      } else {
        // Validar que cada animal tenga unidades y estado
        animalEntries.forEach((animal: any, index: number) => {
          const ut = animal?.ut;
          const statusId = animal?.statusId;
          logAnimals(`   Animal [${index}]: ut="${ut}", statusId="${statusId}"`);

          if (ut === undefined || ut === null || ut === '' || Number(ut) <= 0) {
            logAnimals(`❌ ERROR: Animal sin unidades válidas`);
            animalsErrorCount++;
            errors.push({
              tab: getTabLabel(animalsTabKey),
              tabKey: animalsTabKey,
              field: `animalsTable.${index}.ut`,
              message: i18n.t('validation:animals.unitRequired', 'Es necesario indicar las unidades del animal'),
            });
          }

          if (!statusId) {
            logAnimals(`❌ ERROR: Animal sin estado seleccionado`);
            animalsErrorCount++;
            errors.push({
              tab: getTabLabel(animalsTabKey),
              tabKey: animalsTabKey,
              field: `animalsTable.${index}.statusId`,
              message: i18n.t('validation:animals.statusRequired', 'Es necesario seleccionar el estado del animal'),
            });
          }
        });
      }
    } else {
      logAnimals('ℹ️ Switch desactivado - no se requieren animales');
    }
  } else {
    logAnimals('ℹ️ No hay datos de animales en sessionStorage - SKIP');
  }

  logAnimals(`📊 RESUMEN ANIMALES: ${animalsErrorCount} errores`);

  if (animalsErrorCount > 0) {
    tabsWithErrors.push({
      tabKey: animalsTabKey,
      tabLabel: getTabLabel(animalsTabKey),
      hasErrors: true,
      errorCount: animalsErrorCount,
    });
  }

  // ═══════════════════════════════════════════════════════════════
  // 7. VALIDAR PARTIDAS PRESUPUESTARIAS
  // ═══════════════════════════════════════════════════════════════
  logBudgetItems('═══════════════════════════════════════════════════════════════');
  logBudgetItems('7️⃣ VALIDANDO PARTIDAS PRESUPUESTARIAS');
  logBudgetItems('═══════════════════════════════════════════════════════════════');

  // Un rol ciego a partidas (capataz, tablet) nunca debe recibir errores de
  // esta pestaña: el modal de errores ofrece navegar a la pestaña culpable, y
  // esa pestaña le está prohibida — lo empujaría contra su propia
  // prohibición. Con restos rancios en sessionStorage (otro flujo previo en la
  // misma pestaña del navegador) podía ocurrir. No se valida lo que no se ve.
  const budgetItemsData = roleCanViewBudgets(
    getSessionStorageUser()?.user?.rolActive
  )
    ? getFormData(keys.budgetItems)
    : null;
  const budgetItemsTabKey = 'budget-items';
  let budgetItemsErrorCount = 0;

  if (budgetItemsData) {
    logBudgetItems('✅ Datos de partidas encontrados');
    logBudgetItems(`   isBudgetItemsTableDisplayed: ${budgetItemsData.isBudgetItemsTableDisplayed}`);
    logBudgetItems(`   budgetItemsTable keys: ${Object.keys(budgetItemsData.budgetItemsTable || {}).length}`);

    // Solo validar si el switch está activado
    if (budgetItemsData.isBudgetItemsTableDisplayed === 'true') {
      const budgetItemsTable = budgetItemsData.budgetItemsTable || {};
      const budgetItemEntries = Object.values(budgetItemsTable);

      logBudgetItems(`   Partidas añadidas: ${budgetItemEntries.length}`);

      // Validar que hay al menos una partida
      if (budgetItemEntries.length === 0) {
        logBudgetItems('❌ ERROR: Switch activado pero no hay partidas');
        budgetItemsErrorCount++;
        errors.push({
          tab: getTabLabel(budgetItemsTabKey),
          tabKey: budgetItemsTabKey,
          field: 'budgetItemsTable',
          message: i18n.t('validation:budgetItems.atLeastOne', 'Es necesario añadir al menos una partida presupuestaria'),
        });
      } else {
        // Validar que cada partida tenga cantidad válida
        budgetItemEntries.forEach((item: any, index: number) => {
          const quantity = item?.quantity;
          logBudgetItems(`   Partida [${index}]: quantity="${quantity}"`);

          if (quantity === undefined || quantity === null || quantity === '' || Number(quantity) <= 0) {
            logBudgetItems(`❌ ERROR: Partida sin cantidad válida`);
            budgetItemsErrorCount++;
            errors.push({
              tab: getTabLabel(budgetItemsTabKey),
              tabKey: budgetItemsTabKey,
              field: `budgetItemsTable.${index}.quantity`,
              message: i18n.t('validation:budgetItems.quantityRequired', 'Es necesario indicar la cantidad de la partida'),
            });
          }
        });
      }
    } else {
      logBudgetItems('ℹ️ Switch desactivado - no se requieren partidas');
    }
  } else {
    logBudgetItems('ℹ️ No hay datos de partidas en sessionStorage - SKIP');
  }

  logBudgetItems(`📊 RESUMEN PARTIDAS: ${budgetItemsErrorCount} errores`);

  if (budgetItemsErrorCount > 0) {
    tabsWithErrors.push({
      tabKey: budgetItemsTabKey,
      tabLabel: getTabLabel(budgetItemsTabKey),
      hasErrors: true,
      errorCount: budgetItemsErrorCount,
    });
  }

  // ═══════════════════════════════════════════════════════════════
  // RESUMEN FINAL
  // ═══════════════════════════════════════════════════════════════
  logMain('═══════════════════════════════════════════════════════════════════════');
  logMain('📊 RESUMEN FINAL DE VALIDACIÓN');
  logMain('═══════════════════════════════════════════════════════════════════════');
  logMain(`   Total errores: ${errors.length}`);
  logMain(`   isValid: ${errors.length === 0}`);
  logMain(`   Tabs con errores: ${tabsWithErrors.map(t => t.tabKey).join(', ') || 'ninguna'}`);

  errors.forEach((error, index) => {
    logMain(`   Error [${index}]: Tab=${error.tab}, Campo=${error.field}, Mensaje=${error.message}`);
  });

  logMain('🔍 Resultado de validación:', {
    isValid: errors.length === 0,
    totalErrors: errors.length,
    tabsWithErrors: tabsWithErrors.map(t => t.tabKey),
  });

  return {
    isValid: errors.length === 0,
    errors,
    tabsWithErrors,
  };
};

/**
 * Reglas de ORDEN de fechas de los esquemas (campo → nombre del test): fin ≥
 * inicio, llegada anterior al fin (rama `ft/arrival-before-end`) y llegada no
 * anterior al inicio (3-nov). Son las únicas que «Guardar» comprueba.
 */
const DATE_ORDER_RULES = [
  { field: 'registerEndDate', test: 'end-after-start' },
  { field: 'registerArrivalDate', test: 'arrival-after-start' },
  { field: 'registerArrivalDate', test: 'arrival-before-end' },
] as const;

/**
 * Errores de las reglas de orden de fechas, con el esquema de su pestaña: las
 * mismas reglas que aplica «Validar», sin copiarlas. Solo cuentan esos tests:
 * un campo vacío o con un tipo raro no es una fecha incoherente.
 */
const dateOrderErrors = async (
  schema: { validateAt: (path: string, value: unknown, options?: object) => Promise<unknown> },
  data: Record<string, unknown>,
  context?: object
): Promise<Array<{ path?: string; message: unknown }>> => {
  const found: Array<{ path?: string; message: unknown }> = [];
  for (const rule of DATE_ORDER_RULES) {
    try {
      await schema.validateAt(rule.field, data, { abortEarly: false, context });
    } catch (err: any) {
      const all = err?.inner?.length ? err.inner : [err];
      found.push(...all.filter((e: any) => e?.type === rule.test));
    }
  }
  return found;
};

/**
 * «Guardar» en la validación (12-oct, decisión del usuario): no exige lo
 * obligatorio —se puede guardar a medias— pero SÍ frena las fechas
 * incoherentes (fin anterior al inicio, llegada no anterior al fin), igual
 * en la actuación normal y en la básica:
 * - normal: las de Operarios (`createOperatorsAndVehicleSchema`);
 * - básica: las del horario de «Datos Act» (`EditingActionSchema`), con el
 *   mismo contexto que el formulario y que «Validar»: si quien guarda no puede
 *   editar ese horario, no frena.
 * Devuelve lo mismo que `validateAllFormsFromSessionStorage`, así que sale el
 * mismo aviso «Errores de validación» apuntando a la pestaña.
 */
export const validateDateCoherenceFromSessionStorage = async (
  options: ValidationOptions
): Promise<ValidationResult> => {
  const {
    isNewAction,
    showHoursType = true,
    skipOperatorsValidation = false,
  } = options;
  const keys = sessionFormKeys(isNewAction);
  const errors: ValidationError[] = [];
  const tabsWithErrors: TabValidationStatus[] = [];
  const collect = (
    tabKey: string,
    found: Array<{ path?: string; message: unknown }>
  ) => {
    if (!found.length) return;
    found.forEach((error) =>
      errors.push({
        tab: getTabLabel(tabKey),
        tabKey,
        field: error.path || 'general',
        message: normalizeErrorMessage(error.message),
      })
    );
    tabsWithErrors.push({
      tabKey,
      tabLabel: getTabLabel(tabKey),
      hasErrors: true,
      errorCount: found.length,
    });
  };

  // Operarios (actuación normal). La básica no tiene esa pestaña.
  if (!skipOperatorsValidation) {
    const operatorsData =
      getFormData(keys.operatorsMain) ??
      getFormDataUncompressed(keys.operatorsInterno);
    if (operatorsData) {
      collect(
        'operators-and-vehicles',
        await dateOrderErrors(
          createOperatorsAndVehicleSchema(showHoursType),
          operatorsData
        )
      );
    }
  }

  // Horario de la básica en «Datos Act» (la normal no lleva fechas aquí).
  const editionData = getFormData(keys.edition);
  if (editionData) {
    collect(
      'edition',
      await dateOrderErrors(
        EditingActionSchema,
        editionData,
        basicScheduleContextFromSession(
          isNewAction ? 'wizard' : 'validation',
          editionData
        )
      )
    );
  }

  return { isValid: errors.length === 0, errors, tabsWithErrors };
};

/**
 * Genera un mensaje resumen de las tabs con errores
 */
export const generateValidationSummary = (
  tabsWithErrors: TabValidationStatus[]
): string => {
  if (tabsWithErrors.length === 0) return '';

  const tabNames = tabsWithErrors.map((t) => t.tabLabel).join(', ');
  return i18n.t(
    'validation:summary.tabsWithErrors',
    `Tens camps per completar a les següents pestanyes: ${tabNames}`,
    { tabs: tabNames }
  );
};
